import isEmpty from 'lodash/isEmpty'
import memoize from 'lodash/memoize'

import {tagsByCategory} from '../../store/lookbuilder/enums'

/**
 * Converts the tags array and reduces tag objects into array of tag names based on
 * the tag type.
 *
 * @param {Object[]} tags Array of tag objects with the keys "name" and "type"
 * @returns {Object} An object of only tag names
 */
export const reduceTags = (tags) =>
  tags.reduce(
    (output, {name, type}) => {
      switch (type) {
        case 'category':
          return {...output, subcategories: [...output.subcategories, name]}
        case 'compatibility':
          return {...output, compatibilities: [...output.compatibilities, name]}
        default:
          return output
      }
    },
    {
      compatibilities: [],
      subcategories: [],
    },
  )

/**
 * Updates a product object that is retrieved from the Product Service to be
 * more useful for our data manipulations within Redux state.
 *
 * @param {Object} product Product data in object form
 * @return {Object} A product object with better key:value pairs
 */
export const normalizeProductObject = (product) => {
  let normalizedProduct = {...product}
  if (product.tags) {
    normalizedProduct = {
      ...normalizedProduct,
      ...reduceTags(product.tags),
    }
  }
  if (product.setChildren) {
    normalizedProduct.setChildren = product.setChildren.map((setChild) => ({
      ...setChild,
      childSet: normalizeProductObject(setChild.childSet),
    }))
  }
  return normalizedProduct
}

/**
 * Given a product, returns its still-life images, if they exist
 *
 * @param {Object} product Product data in object form
 * @param {Object[]} [product.setImages] Array of product image data in object form
 * @returns {Object[]} Array of image data in object form
 */
export const getStillLifeImages = ({setImages = []}) =>
  setImages.filter(({contentType}) => contentType === 'still-life')

/**
 * Tests whether a given product or all of its children have valid still-life images
 * Does not apply to certain accessories which have no flat lay. Assumes a normalized
 * product object.
 *
 * @param {Object} product Product data in object form
 * @return {Boolean} Whether the product object contains valid still life images
 */
export const productHasValidStillLifeImages = (product) => {
  const categoryWhitelist = ['cufflinks', 'shirt studs', 'tie clip']
  if (product.subcategories.some((subcategory) => categoryWhitelist.includes(subcategory))) {
    return true
  }
  if (product.setChildren.length) {
    return product.setChildren.every(({childSet}) => !isEmpty(getStillLifeImages(childSet)))
  }
  return !isEmpty(getStillLifeImages(product))
}

/**
 * Updates the product objects we get from the server to better suit our needs
 * on the front end.
 *
 * @param {Array} products An array of product objects
 * @returns {Array}
 */
export const normalizeProductObjects = (products) =>
  products.map(normalizeProductObject).filter(productHasValidStillLifeImages)

/**
 * Returns a normalized object of the array of products returned from the API.
 *
 * @param products {Object[]} Array of product data returned from fetching products by category
 * @param products.slug {String} Unique product slug for each product returned from the API
 * @returns {Object} Normalized object containing the products organized by their slug
 */
export const normalizeProductsByCategory = (products) =>
  normalizeProductObjects(products).reduce(
    (output, product) => ({
      ...output,
      productsBySlug: {...output.productsBySlug, [product.slug]: product},
      allProductSlugs: [...output.allProductSlugs, product.slug],
    }),
    {
      productsBySlug: {},
      allProductSlugs: [],
    },
  )

/**
 * Returns the category that tags provided are associated to.
 *
 * @returns {String} Name of category the tags are associated with.
 * @throws {Error} No association found between tags provided and a product category.
 */
export const getCategoryByTags = memoize((tags) => {
  const result = Object.entries(tagsByCategory).find(([, associatedTags]) =>
    tags.some((tag) => associatedTags.some((associatedTag) => tag === associatedTag)),
  )

  if (result.length === 0) {
    throw new Error('Unable to associate tags to product category.')
  }

  return result[0]
})
